import { ShieldAlert } from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";
import { PageHeaderFrame } from "@/components/layout/page-header-frame";
import { AccountabilitySection } from "@/components/accountability/accountability-section";
import { DriWorkloadSection } from "@/components/accountability/dri-workload-section";
import { BlockingSection } from "@/components/accountability/blocking-section";
import { LoadLimitSection } from "@/components/accountability/load-limit-section";
import { CandidateAgingSection } from "@/components/accountability/candidate-aging-section";
import { useAccountabilityOverview } from "@/hooks/useAccountabilityOverview";
import { usePeopleBoard } from "@/hooks/usePeopleBoard";
import { useCandidates } from "@/hooks/useCandidates";

/** Inline stat inside the description sentence (OKR-header pattern) — none of these are clickable. */
const Stat = ({ value, warn }: { value: number; warn?: boolean }) => (
  <span className={warn ? "font-medium text-amber-600 dark:text-amber-500" : "font-medium text-foreground"}>{value}</span>
);

/**
 * Admin-only overview: who is DRI on what (ranked by workload), and who is currently blocking
 * others. Read-only v1. The page itself never scrolls — it fills the viewport below the navbar
 * and each section scrolls independently, so both lists stay in view side by side.
 */
const Accountability = () => {
  const { data, isLoading, isError } = useAccountabilityOverview();
  const { people } = usePeopleBoard();
  const { data: candidatesData } = useCandidates();

  const dris = data?.dris ?? [];
  const blockers = data?.blockers ?? [];
  const driTaskTotal = dris.reduce((sum, e) => sum + e.taskCount, 0);
  const blockedTaskTotal = blockers.reduce((sum, e) => sum + e.blockedCount, 0);
  const overLimitCount = people.filter((p) => p.focus.length > 1 || p.queue.length > 2).length;
  const candidatesCount = candidatesData?.total ?? 0;

  const overviewError = isError ? (
    <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
      Failed to load accountability data. Please try again later.
    </div>
  ) : null;

  return (
    <PageShell className="flex flex-col lg:h-[calc(100vh-4rem)] lg:overflow-hidden">
      <PageHeaderFrame
        icon={ShieldAlert}
        title="Accountability"
        description={
          !isLoading && !isError && dris.length > 0 ? (
            <>
              <Stat value={dris.length} /> {dris.length === 1 ? "DRI" : "DRIs"}
              {" · "}
              <Stat value={driTaskTotal} /> active {driTaskTotal === 1 ? "task" : "tasks"}
              {blockers.length > 0 && (
                <>
                  {" · "}
                  <Stat value={blockers.length} warn /> {blockers.length === 1 ? "person" : "people"} blocking
                  {" · "}
                  <Stat value={blockedTaskTotal} warn /> {blockedTaskTotal === 1 ? "task" : "tasks"} stuck
                </>
              )}
              {overLimitCount > 0 && (
                <>
                  {" · "}
                  <Stat value={overLimitCount} warn /> over limit
                </>
              )}
              {candidatesCount > 0 && (
                <>
                  {" · "}
                  <Stat value={candidatesCount} warn /> {candidatesCount === 1 ? "candidate" : "candidates"}
                </>
              )}
            </>
          ) : (
            "Who owns what, and who is currently blocking others."
          )
        }
      />

      {/* The overview endpoint only feeds the first two panels; the other two have their own queries. */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-2 lg:grid-rows-2">
        <AccountabilitySection title="DRIs">
          {overviewError ?? <DriWorkloadSection entries={dris} isLoading={isLoading} />}
        </AccountabilitySection>

        <AccountabilitySection title="Blocking">
          {overviewError ?? <BlockingSection entries={blockers} isLoading={isLoading} />}
        </AccountabilitySection>

        <AccountabilitySection title="Over Load Limit">
          <LoadLimitSection />
        </AccountabilitySection>

        <AccountabilitySection title="Candidate Aging">
          <CandidateAgingSection />
        </AccountabilitySection>
      </div>
    </PageShell>
  );
};

export default Accountability;
